import CancelableApi from "./CancelableApi";
import CancelableAxiosInstance from "./CancelableAxiosInstance";
import { LetterBadge } from "../service/LetterBadgeManager";
import { PageResult } from "./PageResult";
import { Query } from "./Query";
import { Rectangle } from "../util/RectangleUtility";
import { Marker } from "./EvalWorkflowApi";
import { CodeLabel } from "./GrammarApi";
import { Page } from "../component/Pagination";

export interface RegionGroup {
  regions?: Array<Region>;
  _badge?: LetterBadge;
  showRegion?: boolean;
}

export interface Observation extends RegionGroup {
  id?: string;
  type: string;
  state?: string;
  excludedReason?: string;
  cardinality?: number;
  source?: string;
  params?: Array<Param>;
  level: string;
  tags?: Array<string>;
  history?: Array<HistoryItem>;
  validatedBy?: Array<string>;
}

export interface HistoryItem {
  author: string;
  role: string;
  state: string;
  excludedReason: string;
  workflowId: string;
  cardinality: number;
  value: string;
  tsp: string;
  level: string;
  secDuration: number;
}

export enum RegionType {
  OBSERVATION = "observation",
  NEGATIVE = "negative",
  // For Ground truth workflow
  OBSERVATION_GT = "observation_gt",
  AI = "ai",
}

export interface ImageRectangle extends Rectangle {
  imageId: string;
}

export interface Region extends ImageRegion {
  _type?: RegionType;
  obsIdx?: number;
  obsType?: string;
  confidence?: number;
  active?: boolean;
  attenuated?: boolean;
  _selected?: boolean;
  availableTags?: CodeLabel[];
}

export interface AiResultThresholds {
  doubt: number;
  fract: number;
}

export interface AiRegion extends Rectangle {
  confidence: number;
  _selected?: boolean;
  type: string;
}

export interface ImageRegion extends ImageRectangle {
  badge?: LetterBadge;
}

export interface PassRegion extends ImageRegion {
  passIdx: number;
}

export interface PassMarker extends Marker {
  passIdx: number;
  _color: string;
}

export interface NegativeRegion extends Region {
  tags: string[];
}

export interface Negative {
  type: string;
  regions: Array<NegativeRegion>;
}

export interface Study {
  id: string;
  state: string;

  series: Array<Series>;
  negatives: Array<Negative>;

  indication: string;
  observations: Array<Observation>;
  patientSex: string;
  patientAge: number;
  studyDescription: string;
  studyDate: string;
  comment?: string;
  reportText?: string;
  tags: Array<string>;
}

export interface Param {
  id?: string;
  type: string;
  value?: string;
  state?: string;
  source?: string;
  history?: Array<HistoryItem>;
  validatedBy?: Array<string>;
}

export interface Series {
  seriesNumber: number;
  modality: string;
  bodyPartExamined: string;
  images: Array<Image>;
}

export interface Image {
  id: string;
  path: string;
  thumbnailPath: string;
  tags: Array<string>;
  aiPasses: Array<AiPass>;
}

export interface AiPass {
  aiRegions: Array<AiRegion>;
  modelHash: string;
  created: string;
  type: string;
}

export interface StudyQuery extends Query {
  from?: string;
  to?: string;
}

export default class StudyApi extends CancelableApi {
  static with(axios: CancelableAxiosInstance) {
    return new StudyApi(axios);
  }

  searchStudies(page: Page, query: StudyQuery): Promise<PageResult<Study>> {
    return this.client.post("/studies/search", { page: page.current, size: page.size, ...query }).then((resp) => {
      return resp.data;
    });
  }

  getStudy(id: string): Promise<Study> {
    return this.client.get("/studies/" + id).then((response) => response.data);
  }

  saveStudy(study: Study): Promise<Study> {
    return this.client.post("/studies/" + study.id, study).then((response) => response.data);
  }

  newObservation(study: Study, type: string): Promise<Observation> {
    return this.client
      .get("/studies/" + study.id + "/new-observation", { params: { type } })
      .then((response) => response.data);
  }
}

export const EXCLUDED_REASONS = [
  { value: "notTrauma", text: "Non trauma" },
  { value: "oldLesion", text: "Ancienne lésion" },
  { value: "materialLesion", text: "Fracture de matériel" },
  { value: "platerLesion", text: "Sous plâtre / Atelle / Bandage" },
  { value: "staffCase", text: "Cas de staff" },
  { value: "occultFract", text: "Fracture occulte" },
  { value: "headLesion", text: "Lésion de la tête" },
  { value: "other", text: "Autre" },
  { value: "spineLesion", text: "Lésion de la colonne", deprecated: true },
  { value: "coccyxLesion", text: "Lésion du coccyx", deprecated: true },
  { value: "ribLesion", text: "Lésion du gril costal / Sternum", deprecated: true },
];
